import _Vue from "vue";
import { Brand } from "@/api/braendz/models/Brand";
import { niceClassIcons } from "@/models/NiceClasses";
import { BrandAddress, BrandIndexItem, BrandQueryResultItem, MasterDataItem, Product, PropertyDelta } from "@/api/braendz";
import { getBrandLogoUrl, getBrandLogoUrlByRelativeUrl } from "@/logic/Brand";
import { brandTypeIcons } from "@/models/BrandTypes";
import {
    brandStateCategoryColors,
    brandStateCategoryIcons,
} from "@/models/BrandStateCategories";
import { IdTokenClaim } from "@/models/IdTokenClaim";
import { AccountInfo } from "@azure/msal-browser";
import initials from "initials";
import { Delta } from "@/models/Delta";

export class Braendz {
    
    public registrationOfficeCodeFlag(key: string | undefined | null): string | null {
        switch (key?.toLowerCase()) {
            case "de":
                return require("@/assets/flags/de.png");
            case "eu":
                return require("@/assets/flags/eu.png");
            case "em":
                return require("@/assets/flags/eu.png");
            case "fr":
                return require("@/assets/flags/fr.png");
            case "at":
                return require("@/assets/flags/at.png");
            case "es":
                return require("@/assets/flags/es.png");
            case "ch":
                return require("@/assets/flags/ch.png");
            case "us":
                return require("@/assets/flags/us.png");
            case "wo":
                return require("@/assets/flags/wipo.png");
            default:
                return null;
        }
    }

    public isWipoOffice(office: string | MasterDataItem | null | undefined): boolean {
        // Extract string key from MasterDataItem if needed
        let officeKey: string | null | undefined = null;
        
        if (typeof office === 'string') {
            officeKey = office;
        } else if (office && typeof office === 'object' && 'key' in office) {
            officeKey = office.key;
        }
        
        // Check if office key is 'wo' (case insensitive)
        return officeKey?.toLowerCase() === 'wo';
    }

    public getBrandLogoUrl(brand: BrandIndexItem | Brand | null | undefined): string | null {
        return getBrandLogoUrl(brand);
    }

    public getBrandLogoUrlByRelativeUrl(url: string): string {
        return getBrandLogoUrlByRelativeUrl(url);
    }

    public getNiceClassIcon(key?: string): string | undefined {
        return niceClassIcons.find((i) => i.key === key)?.icon;
    }

    public getBrandTypeIcon(key?: string): string | undefined {
        return brandTypeIcons.find((i) => i.key === key)?.icon;
    }

    public getBrandStateCategoryIcon(key?: string): string | undefined {
        return brandStateCategoryIcons.find((i) => i.key === key)?.icon;
    }

    getBrandStateCategoryColor(key?: string): string | undefined {
        return brandStateCategoryColors.find((i) => i.key === key)?.color;
    }

    public getAddressLine(address: BrandAddress | null | undefined): string {
        const lines = this.getAddressLines(address);
        return lines.join(", ");
    }

    public getAddressLines(address: BrandAddress | null | undefined): string[] {
        const result: string[] = [];

        if (address?.addressLine) {
            address.addressLine.split(/[,;]\s*/).forEach((i) => result.push(i));
        } else {
            if (address?.companyName) {
                result.push(address.companyName);
            } else if (address?.name) {
                result.push(address.name);
            }

            if (address?.firstName || address?.lastName) {
                let name = address.firstName ?? "";
                if (address.lastName) {
                    if (name) {
                        name += " ";
                    }
                    name += address.lastName;
                }
                result.push(name);
            }

            if (address?.street) {
                result.push(address.street);
            }

            if (address?.postOfficeBox || address?.postalCode || address?.city) {
                let location = address.postalCode ?? address.postOfficeBox ?? "";

                if (address.city) {
                    if (location) {
                        location += " ";
                    }
                    location += address.city;
                }
                result.push(location);
            }

            if (address?.state) {
                result.push(address.state);
            }

            if (address?.country || address?.countryCode) {
                result.push(address.country ?? address.countryCode ?? "");
            }
        }

        return result.filter(
            (value, index, array) => array.indexOf(value) === index
        );
    }

    public getInitials(userAccount: AccountInfo | null): string | null {
        if (userAccount) {
            if (userAccount.idTokenClaims) {
                let firstName: string | undefined = undefined;
                let lastName: string | undefined = undefined;

                if ((userAccount.idTokenClaims as IdTokenClaim).given_name) {
                    firstName = (userAccount.idTokenClaims as IdTokenClaim).given_name;
                }

                if ((userAccount.idTokenClaims as IdTokenClaim).family_name) {
                    lastName = (userAccount.idTokenClaims as IdTokenClaim).family_name;
                }

                if (firstName && lastName) {
                    return initials(`${firstName} ${lastName}`);
                } else if (lastName) {
                    return initials(lastName);
                } else if (firstName) {
                    return initials(firstName);
                }
            } else if (userAccount.name) {
                return initials(userAccount.name);
            }
            return initials(userAccount.username);
        }
        return null;
    }

    public getDisplayName(userAccount: AccountInfo | null): string | null {
        if (userAccount) {
            if (userAccount.idTokenClaims) {
                let firstName: string | undefined = undefined;
                let lastName: string | undefined = undefined;

                if ((userAccount.idTokenClaims as IdTokenClaim).given_name) {
                    firstName = (userAccount.idTokenClaims as IdTokenClaim).given_name;
                }

                if ((userAccount.idTokenClaims as IdTokenClaim).family_name) {
                    lastName = (userAccount.idTokenClaims as IdTokenClaim).family_name;
                }

                if (firstName && lastName) {
                    return `${firstName} ${lastName}`;
                } else if (lastName) {
                    return lastName;
                } else if (firstName) {
                    return firstName;
                }
            } else if (userAccount.name) {
                return userAccount.name;
            }
        }
        return null;
    }

    public getBrandData(brand: BrandQueryResultItem | BrandIndexItem | Brand | null | undefined): Brand | BrandIndexItem | null {
        if(!brand) {
            return null;
        }
        else if("indexItem" in brand) {
            return brand.indexItem ?? null;
        }
        else if("name" in brand) {
            return brand;
        }
        return null;
    }

    public getBrandId(brand: BrandQueryResultItem | BrandIndexItem | Brand | null | undefined): string | null {
        if (!brand) {
            return null;
        }
        else if ("indexItem" in brand) {
            return brand.indexItem?.brandId ?? null;
        }
        else if ("name" in brand) {
            return brand.id ?? null;
        }
        return null;
    }

    public getPropertyDelta(deltaSource: BrandQueryResultItem | BrandIndexItem | Brand | PropertyDelta[] | null | undefined, property: string, value?: any): Delta {
        // Default return value
        const defaultResult: Delta = { property: property, deltaType: null, originalValue: null, currentValue: null };

        if(!deltaSource) {
            return defaultResult;
        }

        // Get propertyDeltas array from different source types
        let propertyDeltas: PropertyDelta[] | null = null;
        
        if(Array.isArray(deltaSource)) {
            // Direct PropertyDelta[] array
            propertyDeltas = deltaSource;
        } else if("propertyDeltas" in deltaSource && deltaSource.propertyDeltas && Array.isArray(deltaSource.propertyDeltas)) {
            // Brand object with propertyDeltas property
            propertyDeltas = deltaSource.propertyDeltas;
        }

        if(!propertyDeltas) {
            return defaultResult;
        }

        // Find all matching property deltas
        const matchingDeltas = propertyDeltas.filter((delta: PropertyDelta) => {
            // First check if property name matches
            if (delta.property !== property) {
                return false;
            }
            
            // If value parameter is provided, also check for value matches
            if (value !== undefined) {
                // Handle GoodOrService objects with special comparison
                if (value && typeof value === 'object' && 'description' in value && 'niceClass' in value) {
                    const compareGoodOrService = (deltaValue: any) => {
                        if (!deltaValue || typeof deltaValue !== 'object') return false;
                        return deltaValue.description == value.description && 
                               deltaValue.niceClass == value.niceClass?.key;
                    };
                    return compareGoodOrService(delta.originalValue) || compareGoodOrService(delta.currentValue);
                }
                // Default comparison for other types
                return delta.originalValue == value || delta.currentValue == value;
            }
            
            // If no value parameter, just match by property name
            return true;
        });

        if(matchingDeltas.length === 0) {
            return defaultResult;
        }

        // If multiple findings, return 'Changed'
        if(matchingDeltas.length > 1) {
            return { 
                property: property,
                deltaType: 'Changed', 
                originalValue: null,
                currentValue: null
            };
        }

        // Single match found
        const delta = matchingDeltas[0];
        return {
            property: property,
            deltaType: (delta.deltaType as 'Added' | 'Removed' | 'Changed' | 'None') ?? null,
            originalValue: delta.originalValue ?? null,
            currentValue: delta.currentValue ?? null
        };
    }
}

export const braendz = new Braendz();

export function BraendzPlugin(Vue: typeof _Vue): void {
    Vue.prototype.$braendz = braendz;
}

declare module "vue/types/vue" {
    interface Vue {
        $braendz: Braendz;
    }
}
